import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaShareAlt,
    FaRegSmile,
    FaBookmark,
    FaUserPlus,
    FaFlag,
    FaHeart,
    FaTimes,
    FaChevronLeft,
    FaChevronRight,
    FaStar,
    FaUndo,
    FaBolt,
    FaEyeSlash,
    FaExpand,
} from "react-icons/fa";
import { useSpring, animated } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import "./SuggestedProfiles.css";
import image63 from "../img/user.png";
import emailjs from "emailjs-com";
import Like from "../LikeFeature/Like";
import MatchModal from "../MatchModal/MatchModal";

interface Profile {
    userId: string;
    firstName: string;
    gender: string;
    age: number;
    registeredBy: string;
    country: string;
    occupation?: string;
    profilePictureUrl?: string;
    photos?: string[];
}

interface ReportData {
    reason: string;
    details: string;
}

interface SwipeableProfileCardProps {
    profile: Profile;
    isActive: boolean;
    index: number;
    onSwipe: (direction: string, profile: Profile) => void;
    children: React.ReactNode;
}

const SwipeableProfileCard: React.FC<SwipeableProfileCardProps> = ({
    profile,
    isActive,
    index,
    onSwipe,
    children
}) => {
    const [{ x, y, rotate, scale, opacity }, api] = useSpring(() => ({
        x: 0,
        y: 0,
        rotate: 0,
        scale: isActive ? 1 : 0.95,
        opacity: isActive ? 1 : 0.8,
        config: { tension: 400, friction: 40 },
    }));

    const [swipeDirection, setSwipeDirection] = useState<string | null>(null);

    React.useEffect(() => {
        api.start({
            scale: isActive ? 1 : 0.95,
            opacity: isActive ? 1 : 0.8,
            immediate: false,
        });
    }, [isActive, api]);

    const bind = useDrag(
        (state: any) => {
            const { down, movement, velocity, direction: dir } = state;
            const mx = movement[0];
            const my = movement[1];
            const vx = velocity[0];

            if (!isActive) return;

            const trigger = Math.abs(vx) > 0.4 || Math.abs(mx) > window.innerWidth * 0.35;

            if (Math.abs(mx) > 50) {
                setSwipeDirection(mx > 0 ? 'right' : 'left');
            } else {
                setSwipeDirection(null);
            }

            if (!down && trigger) {
                const direction = mx > 0 ? 'right' : 'left';
                const flyOutX = direction === 'left' ? -window.innerWidth * 1.5 : window.innerWidth * 1.5;

                api.start({
                    x: flyOutX,
                    y: my,
                    rotate: mx / 8,
                    scale: 1.1,
                    opacity: 0,
                    config: { duration: 300 },
                    onRest: () => {
                        onSwipe(direction, profile);
                        setSwipeDirection(null);
                    },
                });
            } else if (down) {
                api.start({
                    x: mx,
                    y: my * 0.3,
                    rotate: mx / 12,
                    scale: 1.05,
                    opacity: 1 - Math.abs(mx) / window.innerWidth * 0.3,
                    immediate: true,
                });
            } else {
                api.start({
                    x: 0,
                    y: 0,
                    rotate: 0,
                    scale: 1,
                    opacity: 1,
                });
                setSwipeDirection(null);
            }
        },
        { filterTaps: true }
    );

    return (
        <animated.div
            {...(isActive ? bind() : {})}
            style={{
                x,
                y,
                rotate: rotate.to((r) => `${r}deg`),
                scale,
                opacity,
                touchAction: 'none',
                cursor: isActive ? 'grab' : 'default',
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: isActive ? 100 : 99 - index,
                transform: `translateY(${index * 10}px)`,
            }}
            className={`swipeable-card ${isActive ? 'active' : ''}`}
        >
            {swipeDirection && (
                <div className={`swipe-indicator ${swipeDirection}`}>
                    {swipeDirection === 'right' ? (
                        <>
                            <FaHeart size={60} />
                            <span>LIKE</span>
                        </>
                    ) : (
                        <>
                            <FaTimes size={60} />
                            <span>NOPE</span>
                        </>
                    )}
                </div>
            )}
            {children}
        </animated.div>
    );
};

const SuggestedProfiles: React.FC = () => {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const currentUserId = localStorage.getItem("userId");
    const [likedProfiles, setLikedProfiles] = useState<Record<string, boolean>>({});
    const [complimentedProfiles, setComplimentedProfiles] = useState<Record<string, string>>({});
    const [notification, setNotification] = useState<string>("");
    const [savedProfiles, setSavedProfiles] = useState<Record<string, boolean>>({});
    const [followedProfiles, setFollowedProfiles] = useState<Record<string, boolean>>({});
    const [showComplimentPopup, setShowComplimentPopup] = useState<boolean>(false);
    const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
    const [selectedCompliment, setSelectedCompliment] = useState<string>("");
    const [showSharePopup, setShowSharePopup] = useState<boolean>(false);
    const [shareLink, setShareLink] = useState<string>("");
    const [reportedProfiles, setReportedProfiles] = useState<Record<string, ReportData>>(() => {
        const storedReports = localStorage.getItem("reportedProfiles");
        return storedReports ? JSON.parse(storedReports) : {};
    });
    const [showReportPopup, setShowReportPopup] = useState<boolean>(false);
    const [selectedReportReason, setSelectedReportReason] = useState<string>("");
    const [currentReportedProfile, setCurrentReportedProfile] = useState<string | null>(null);
    const [reportDetails, setReportDetails] = useState<string>("");
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [swipedProfiles, setSwipedProfiles] = useState<Set<string>>(new Set());
    const [lastSwipedProfile, setLastSwipedProfile] = useState<Profile | null>(null);
    const [canUndo, setCanUndo] = useState<boolean>(false);
    const [hiddenProfiles, setHiddenProfiles] = useState<Set<string>>(new Set());
    const [isProfileExpanded, setIsProfileExpanded] = useState<boolean>(false);
    const [currentPhotoIndexes, setCurrentPhotoIndexes] = useState<Record<string, number>>({});
    const [profilePhotos, setProfilePhotos] = useState<Record<string, string[]>>({});
    const [isSwipeInProgress, setIsSwipeInProgress] = useState<boolean>(false);
    const [showMatchModal, setShowMatchModal] = useState<boolean>(false);
    const [matchedUserName, setMatchedUserName] = useState<string>("");
    const [matchedUserId, setMatchedUserId] = useState<string>("");
    const [matchedUserEmail, setMatchedUserEmail] = useState<string>("");

    useEffect(() => {
        const savedState = sessionStorage.getItem('suggestedProfilesState');
        if (savedState) {
            try {
                const { currentIndex: savedIndex } = JSON.parse(savedState);
                setCurrentIndex(savedIndex);
                sessionStorage.removeItem('suggestedProfilesState');
            } catch (error) {
                console.error('Error restoring state:', error);
            }
        }
    }, []);

    useEffect(() => {
        const fetchAllProfiles = async () => {
            const token = localStorage.getItem("authToken");
            const currentUserId = localStorage.getItem("userId");

            if (!token) {
                console.error("Authentication token not found!");
                return;
            }

            try {
                let allProfiles: Profile[] = [];
                let currentPage = 1;
                let totalPages = 1;

                while (currentPage <= totalPages) {
                    const response = await fetch(
                        `https://nrimarriage.in/api/v1/users/GetUsers?pageNumber=${currentPage}&pageSize=10`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`Failed to fetch profiles: ${response.statusText}`);
                    }

                    const data = await response.json();
                    
                    if (data && data.results) {
                        const filteredProfiles = data.results.filter(
                            (profile: Profile) =>
                                String(profile.userId) !== String(currentUserId)
                        );
                        allProfiles = [...allProfiles, ...filteredProfiles];
                        totalPages = data.totalPages;
                        currentPage++;
                    } else {
                        console.error("Unexpected response format:", data);
                        break;
                    }
                }

                setProfiles(allProfiles);
                console.log(`Total filtered profiles fetched: ${allProfiles.length}`);
            } catch (error: any) {
                console.error("Error fetching profiles:", error.message);
            }
        };

        fetchAllProfiles();
    }, []);

    useEffect(() => {
        const fetchProfileImages = async (userId: string) => {
            const token = localStorage.getItem("authToken");
            if (!token) return;

            try {
                const response = await fetch(`https://nrimarriage.in/api/v1/users/GetUserImagesWithProfile?userId=${userId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) return;

                const imagesData = await response.json();
                const mainImage = imagesData.profileImage?.imageUrl;
                const additionalImages = imagesData.allImages?.map((img: any) => img.imageUrl) || [];

                const allPhotos = [mainImage, ...additionalImages].filter(Boolean);

                setProfilePhotos(prev => ({
                    ...prev,
                    [userId]: allPhotos
                }));

                if (!currentPhotoIndexes[userId]) {
                    setCurrentPhotoIndexes(prev => ({
                        ...prev,
                        [userId]: 0
                    }));
                }
            } catch (err) {
                console.error('Error fetching profile images:', err);
            }
        };

        const visible = profiles.filter(p => !swipedProfiles.has(p.userId) && !hiddenProfiles.has(p.userId));
        visible.slice(currentIndex, currentIndex + 3).forEach(profile => {
            if (!profilePhotos[profile.userId]) {
                fetchProfileImages(profile.userId);
            }
        });
    }, [profiles, swipedProfiles, hiddenProfiles, profilePhotos, currentPhotoIndexes, currentIndex]);

    const triggerNotification = (message: string) => {
        setNotification(message);
        setTimeout(() => {
            setNotification("");
        }, 4000);
    };

    const handleLikeChange = async (userId: string, newLikeStatus: boolean, isMatch?: boolean) => {
        setLikedProfiles((prev) => ({
            ...prev,
            [userId]: newLikeStatus,
        }));

        if (newLikeStatus && isMatch) {
            const authToken = localStorage.getItem("authToken");
            if (authToken) {
                try {
                    const userResponse = await fetch(`https://nrimarriage.in/api/v1/users/GetUser?id=${userId}`, {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${authToken}`
                        }
                    });

                    if (userResponse.ok) {
                        const userData = await userResponse.json();
                        setMatchedUserName(userData.firstName || "Matched User");
                        setMatchedUserId(userId);
                        setMatchedUserEmail(userData.email || "");
                        setShowMatchModal(true);
                    }
                } catch (error) {
                    console.error("Error fetching matched user data:", error);
                }
            }
        }

        if (newLikeStatus && visibleProfiles[currentIndex]?.userId === userId) {
            const profile = visibleProfiles[currentIndex];
            setSwipedProfiles(prev => new Set([...prev, profile.userId]));
            setLastSwipedProfile(profile);
            setCanUndo(true);
        }
    };

    const handleComplimentClick = (profileId: string) => {
        setCurrentProfileId(profileId);
        setShowComplimentPopup((prevState) => !prevState);
    };

    const handleComplimentSelect = (compliment: string) => {
        if (!currentProfileId) return;

        setSelectedCompliment(compliment);
        setComplimentedProfiles((prevState) => ({
            ...prevState,
            [currentProfileId]: compliment,
        }));
        setShowComplimentPopup(false);
        triggerNotification(`Compliment sent: "${compliment}"`);
    };

    const handleSave = (profileId: string) => {
        setSavedProfiles((prevState) => {
            const newState = { ...prevState };
            const isSaved = newState[profileId];
            newState[profileId] = !isSaved;
            triggerNotification(isSaved ? "Profile removed from saved!" : "Profile saved for later.");
            return newState;
        });
    };

    const handleFollow = (profileId: string) => {
        setFollowedProfiles((prevState) => {
            const newState = { ...prevState };
            const isFollowed = newState[profileId];
            newState[profileId] = !isFollowed;
            triggerNotification(isFollowed ? "Unfollowed the profile!" : "Followed the profile.");
            return newState;
        });
    };

    const handleShareClick = (profileId: string) => {
        const profileLink = `${window.location.origin}/profile/${profileId}`;
        setShareLink(profileLink);
        setShowSharePopup(true);
        triggerNotification(`Sharing profile link for ${profileId}`);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareLink).then(() => {
            triggerNotification("Link copied to clipboard!");
            setShowSharePopup(false);
        });
    };

    const handleReportClick = (profileId: string) => {
        setCurrentReportedProfile(profileId);
        setSelectedReportReason("");
        setShowReportPopup(true);
    };

    const handleReportSubmit = () => {
        if (!selectedReportReason || !currentReportedProfile) {
            triggerNotification("Please select a reason for reporting.");
            return;
        }

        if (reportedProfiles[currentReportedProfile]) {
            triggerNotification("You have already reported this profile.");
            return;
        }

        const updatedReports = {
            ...reportedProfiles,
            [currentReportedProfile]: {
                reason: selectedReportReason,
                details: reportDetails,
            },
        };

        setReportedProfiles(updatedReports);

        emailjs
            .send(
                "service_zrp7n08",
                "template_7hvlx9l",
                {
                    user_id: currentReportedProfile,
                    report_category: selectedReportReason,
                    report_details: reportDetails,
                },
                "xngP_o5K8s1_S5hap"
            )
            .then(() => {
                triggerNotification("Report submitted successfully! Thank you.");
            })
            .catch(() => {
                triggerNotification("Error: Failed to send the report.");
            });

        setShowReportPopup(false);
        setSelectedReportReason("");
        setReportDetails("");
    };

    const handleLikeAPI = async (likedUserId: string): Promise<boolean> => {
        const authToken = localStorage.getItem("authToken");
        const currentUserId = localStorage.getItem("userId");

        if (!authToken || !currentUserId) {
            triggerNotification("Please log in to like profiles.");
            return false;
        }

        try {
            const response = await fetch(
                `https://nrimarriage.in/api/v1/like/${currentUserId}/${likedUserId}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to like profile: ${response.status}`);
            }

            const mutualResponse = await fetch(
                `https://nrimarriage.in/api/v1/like/${likedUserId}/${currentUserId}/exists`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`,
                    },
                }
            );

            if (mutualResponse.ok) {
                const mutualData: { hasLiked: boolean } = await mutualResponse.json();
                return mutualData.hasLiked;
            }

            return false;
        } catch (error) {
            console.error("Error liking profile:", error);
            triggerNotification("Failed to like profile. Please try again.");
            return false;
        }
    };

    const handleSwipe = async (direction: string, profile: Profile) => {
        if (isSwipeInProgress) return;
        setIsSwipeInProgress(true);

        if (direction === 'right') {
            const isMatch = await handleLikeAPI(profile.userId);

            if (isMatch) {
                const authToken = localStorage.getItem("authToken");
                if (authToken) {
                    try {
                        const userResponse = await fetch(`https://nrimarriage.in/api/v1/users/GetUser?id=${profile.userId}`, {
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${authToken}`
                            }
                        });

                        if (userResponse.ok) {
                            const userData = await userResponse.json();
                            setMatchedUserName(userData.firstName || "Matched User");
                            setMatchedUserId(profile.userId);
                            setMatchedUserEmail(userData.email || "");
                            setShowMatchModal(true);
                        }
                    } catch (error) {
                        console.error("Error fetching matched user data:", error);
                        triggerNotification(`It's a Match! You and ${profile.firstName} liked each other!`);
                    }
                }
            } else {
                triggerNotification(`You liked ${profile.firstName}!`);
            }

            setLikedProfiles((prev) => ({
                ...prev,
                [profile.userId]: true,
            }));
        } else if (direction === 'left') {
            triggerNotification(`Passed on ${profile.firstName}`);
        }

        setSwipedProfiles(prev => new Set([...prev, profile.userId]));
        setLastSwipedProfile(profile);
        setCanUndo(true);

        setTimeout(() => {
            setIsSwipeInProgress(false);
        }, 300);
    };

    const handleNavigate = (direction: 'next' | 'prev') => {
        if (direction === 'next' && currentIndex < visibleProfiles.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else if (direction === 'prev' && currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    const handleUndo = () => {
        if (canUndo && lastSwipedProfile) {
            setSwipedProfiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(lastSwipedProfile.userId);
                return newSet;
            });
            setCurrentIndex(prev => Math.max(0, prev - 1));
            setCanUndo(false);
            setLastSwipedProfile(null);
            triggerNotification('Undo successful!');
        }
    };

    const handleSuperLike = async () => {
        if (isSwipeInProgress) return;
        if (visibleProfiles[currentIndex]) {
            setIsSwipeInProgress(true);
            const profile = visibleProfiles[currentIndex];
            const isMatch = await handleLikeAPI(profile.userId);

            setSwipedProfiles(prev => new Set([...prev, profile.userId]));
            setLastSwipedProfile(profile);
            setCanUndo(true);

            if (isMatch) {
                const authToken = localStorage.getItem("authToken");
                if (authToken) {
                    try {
                        const userResponse = await fetch(`https://nrimarriage.in/api/v1/users/GetUser?id=${profile.userId}`, {
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${authToken}`
                            }
                        });

                        if (userResponse.ok) {
                            const userData = await userResponse.json();
                            setMatchedUserName(userData.firstName || "Matched User");
                            setMatchedUserId(profile.userId);
                            setMatchedUserEmail(userData.email || "");
                            setShowMatchModal(true);
                        }
                    } catch (error) {
                        console.error("Error fetching matched user data:", error);
                        triggerNotification(`It's a Match! You and ${profile.firstName} liked each other!`);
                    }
                }
            } else {
                triggerNotification(`Super Liked ${profile.firstName}!`);
            }

            setLikedProfiles((prev) => ({
                ...prev,
                [profile.userId]: true,
            }));

            setTimeout(() => {
                setIsSwipeInProgress(false);
            }, 300);
        }
    };

    const handleBoost = () => {
        triggerNotification('Boost feature coming soon!');
    };

    const handleHide = () => {
        if (visibleProfiles[currentIndex]) {
            setHiddenProfiles(prev => new Set([...prev, visibleProfiles[currentIndex].userId]));
            setSwipedProfiles(prev => new Set([...prev, visibleProfiles[currentIndex].userId]));
            triggerNotification('Profile hidden');
        }
    };

    const handleOpenProfile = (userId: string) => {
        sessionStorage.setItem('suggestedProfilesState', JSON.stringify({
            currentIndex,
            timestamp: Date.now()
        }));

        navigate(`/profile/${userId}`);
    };

    const toggleProfileExpanded = () => {
        setIsProfileExpanded(prev => !prev);
    };

    const handleNextPhoto = () => {
        if (visibleProfiles.length === 0) return;

        const currentProfile = visibleProfiles[currentIndex];
        const photos = profilePhotos[currentProfile.userId] || [currentProfile.profilePictureUrl || image63];
        const currentPhotoIndex = currentPhotoIndexes[currentProfile.userId] || 0;

        if (photos.length <= 1) {
            triggerNotification('No more photos available!');
            return;
        }

        const nextIndex = (currentPhotoIndex + 1) % photos.length;
        setCurrentPhotoIndexes(prev => ({
            ...prev,
            [currentProfile.userId]: nextIndex
        }));
    };

    const handlePrevPhoto = () => {
        if (visibleProfiles.length === 0) return;

        const currentProfile = visibleProfiles[currentIndex];
        const photos = profilePhotos[currentProfile.userId] || [currentProfile.profilePictureUrl || image63];
        const currentPhotoIndex = currentPhotoIndexes[currentProfile.userId] || 0;

        if (photos.length <= 1) {
            triggerNotification('No more photos available!');
            return;
        }

        const prevIndex = currentPhotoIndex === 0 ? photos.length - 1 : currentPhotoIndex - 1;
        setCurrentPhotoIndexes(prev => ({
            ...prev,
            [currentProfile.userId]: prevIndex
        }));
    };

    const handlePhotoIndicatorClick = (photoIndex: number) => {
        if (visibleProfiles.length === 0) return;

        const currentProfile = visibleProfiles[currentIndex];
        setCurrentPhotoIndexes(prev => ({
            ...prev,
            [currentProfile.userId]: photoIndex
        }));
    };

    const handlePhotoTapZone = (zone: 'left' | 'right', event: React.MouseEvent) => {
        event.stopPropagation();
        if (visibleProfiles.length === 0) return;

        const currentProfile = visibleProfiles[currentIndex];
        const photos = profilePhotos[currentProfile.userId] || [currentProfile.profilePictureUrl || image63];
        const currentPhotoIndex = currentPhotoIndexes[currentProfile.userId] || 0;

        if (photos.length <= 1) return;

        let nextIndex: number;
        if (zone === 'left') {
            nextIndex = currentPhotoIndex === 0 ? photos.length - 1 : currentPhotoIndex - 1;
        } else {
            nextIndex = (currentPhotoIndex + 1) % photos.length;
        }

        setCurrentPhotoIndexes(prev => ({
            ...prev,
            [currentProfile.userId]: nextIndex
        }));
    };

    const visibleProfiles = profiles.filter(p => !swipedProfiles.has(p.userId) && !hiddenProfiles.has(p.userId));

    const compliments = [
        "You're amazing!",
        "Looking great!",
        "I admire your style!",
        "You have a beautiful smile!",
    ];

    const emojis = ["😊", "❤️", "😎", "🌟"];

    return (
        <div className="carousel-container">
            <h2>Suggested Profiles</h2>
            {notification && <div className="notification">{notification}</div>}

            {/* Navigation Controls */}
            <div className="swipe-controls">
                <button
                    className="nav-button prev"
                    onClick={() => handleNavigate('prev')}
                    disabled={currentIndex === 0}
                >
                    <FaChevronLeft size={24} />
                </button>

                <div className="profile-counter">
                    {visibleProfiles.length > 0 ? `${currentIndex + 1} / ${visibleProfiles.length}` : '0 / 0'}
                </div>

                <button
                    className="nav-button next"
                    onClick={() => handleNavigate('next')}
                    disabled={currentIndex >= visibleProfiles.length - 1}
                >
                    <FaChevronRight size={24} />
                </button>
            </div>

            {visibleProfiles.length > 0 ? (
                <div className="card-stack-wrapper">
                    {visibleProfiles.slice(currentIndex, currentIndex + 3).map((profile, index) => (
                        <SwipeableProfileCard
                            key={profile.userId}
                            profile={profile}
                            isActive={index === 0}
                            index={index}
                            onSwipe={handleSwipe}
                        >
                        <div key={profile.userId} className="profile-card20">
                            {(() => {
                                const photos = profilePhotos[profile.userId] || [profile.profilePictureUrl || image63];
                                const currentPhotoIndex = currentPhotoIndexes[profile.userId] || 0;
                                const currentPhoto = photos[currentPhotoIndex] || photos[0] || image63;

                                return (
                                    <>
                                        {photos.length > 1 && (
                                            <div className="photo-indicators">
                                                {photos.map((_, photoIndex) => (
                                                    <div
                                                        key={photoIndex}
                                                        className={`photo-indicator ${photoIndex === currentPhotoIndex ? 'active' : ''}`}
                                                        onClick={() => handlePhotoIndicatorClick(photoIndex)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        <div className="photo-container">
                                            <img
                                                className="profile-card-background clickable"
                                                src={currentPhoto}
                                                alt={`${profile.firstName}`}
                                                loading="lazy"
                                                onClick={() => handleOpenProfile(profile.userId)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </div>
                                    </>
                                );
                            })()}

                            <div className="profile-card-overlay"></div>
                            
                            <div className="profile-content">
                                <div className="profile-info">
                                    <div className="profile-status-indicator">
                                        <span className="status-dot"></span>
                                        <span>Recently active</span>
                                    </div>

                                    <div
                                        className="profile-name-age clickable"
                                        onClick={() => handleOpenProfile(profile.userId)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <h3 className="profile-name">{profile.firstName}</h3>
                                        <span className="profile-age">{profile.age}</span>
                                    </div>

                                    <p className="profile-occupation">
                                        {profile.occupation || profile.registeredBy || profile.gender}
                                    </p>

                                    <p className="profile-location">{profile.country}</p>
                                </div>

                                <div className="profile-actions">
                                    <Like
                                        likedId={profile.userId}
                                        isLiked={likedProfiles[profile.userId]}
                                        onLikeChange={handleLikeChange}
                                    />

                                    <button
                                        className="share-button"
                                        onClick={() => handleShareClick(profile.userId)}
                                    >
                                        <FaShareAlt size={18} />
                                    </button>
                                    
                                    <button
                                        className="compliment-button"
                                        onClick={() => handleComplimentClick(profile.userId)}
                                    >
                                        <FaRegSmile size={18} />
                                    </button>

                                    <button
                                        className={`save-button ${savedProfiles[profile.userId] ? "saved" : ""}`}
                                        onClick={() => handleSave(profile.userId)}
                                    >
                                        <FaBookmark size={18} />
                                    </button>
                                    
                                    <button
                                        className={`follow-button ${followedProfiles[profile.userId] ? "followed" : ""}`}
                                        onClick={() => handleFollow(profile.userId)}
                                    >
                                        <FaUserPlus size={18} />
                                    </button>

                                    <button
                                        className={`report-button ${reportedProfiles[profile.userId] ? "reported" : ""}`}
                                        onClick={() => handleReportClick(profile.userId)}
                                        disabled={!!reportedProfiles[profile.userId]}
                                    >
                                        <FaFlag size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Compliment Popup */}
                            {showComplimentPopup && currentProfileId === profile.userId && (
                                <div className="compliment-popup">
                                    <div className="compliment-header">
                                        <h4>Send a Compliment</h4>
                                        <button onClick={() => setShowComplimentPopup(false)}>X</button>
                                    </div>
                                    <div className="compliment-options">
                                        <h5>Select a Compliment</h5>
                                        <div className="compliment-messages">
                                            {compliments.map((message, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleComplimentSelect(message)}
                                                >
                                                    {message}
                                                </button>
                                            ))}
                                        </div>
                                        <h5>Select an Emoji</h5>
                                        <div className="emoji-selection">
                                            {emojis.map((emoji, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleComplimentSelect(emoji)}
                                                >
                                                    {emoji}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Display the compliment */}
                            {complimentedProfiles[profile.userId] && (
                                <div className="compliment-display">
                                    <p>{`${profile.firstName} has been complimented with: "${complimentedProfiles[profile.userId]}"`}</p>
                                </div>
                            )}
                        </div>
                        </SwipeableProfileCard>
                    ))}
                </div>
            ) : (
                <p>Loading profiles...</p>
            )}

            {/* Desktop Bottom Label Buttons */}
            {visibleProfiles.length > 0 && (
                <div className="desktop-bottom-actions">
                    <button
                        className="desktop-label-btn"
                        onClick={handleHide}
                        title="Hide this profile"
                    >
                        <FaEyeSlash />
                        <span>Hide</span>
                    </button>
                    <button
                        className="desktop-label-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (visibleProfiles[currentIndex]) {
                                handleSwipe('left', visibleProfiles[currentIndex]);
                            }
                        }}
                        title="Pass on this profile"
                    >
                        <FaTimes />
                        <span>Nope</span>
                    </button>
                    <button
                        className="desktop-label-btn desktop-label-like"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (visibleProfiles[currentIndex]) {
                                handleSwipe('right', visibleProfiles[currentIndex]);
                            }
                        }}
                        title="Like this profile"
                    >
                        <FaHeart />
                        <span>Like</span>
                    </button>
                    <button
                        className="desktop-label-btn"
                        onClick={() => handleOpenProfile(visibleProfiles[currentIndex].userId)}
                        title="View full profile"
                    >
                        <FaExpand />
                        <span>Open profile</span>
                    </button>
                    <button
                        className="desktop-label-btn"
                        onClick={handleNextPhoto}
                        title="Next photo"
                    >
                        <FaChevronRight />
                        <span>Next photo</span>
                    </button>
                </div>
            )}

            {/* Mobile Bottom Navigation - Only on mobile/tablet */}
            {visibleProfiles.length > 0 && (
                <div className="mobile-bottom-nav">
                    <div className="like-container">
                        <Like
                            likedId={visibleProfiles[currentIndex]?.userId}
                            isLiked={likedProfiles[visibleProfiles[currentIndex]?.userId]}
                            onLikeChange={handleLikeChange}
                        />
                    </div>

                    <button
                        className="mobile-action-btn share-btn"
                        onClick={() => handleShareClick(visibleProfiles[currentIndex]?.userId)}
                        title="Share"
                    >
                        <FaShareAlt size={24} />
                    </button>

                    <button
                        className="mobile-action-btn compliment-btn"
                        onClick={() => handleComplimentClick(visibleProfiles[currentIndex]?.userId)}
                        title="Compliment"
                    >
                        <FaRegSmile size={24} />
                    </button>

                    <button
                        className={`mobile-action-btn save-btn ${savedProfiles[visibleProfiles[currentIndex]?.userId] ? "saved" : ""}`}
                        onClick={() => handleSave(visibleProfiles[currentIndex]?.userId)}
                        title="Save"
                    >
                        <FaBookmark size={24} />
                    </button>

                    <button
                        className={`mobile-action-btn follow-btn ${followedProfiles[visibleProfiles[currentIndex]?.userId] ? "followed" : ""}`}
                        onClick={() => handleFollow(visibleProfiles[currentIndex]?.userId)}
                        title="Follow"
                    >
                        <FaUserPlus size={24} />
                    </button>

                    <button
                        className={`mobile-action-btn report-btn ${reportedProfiles[visibleProfiles[currentIndex]?.userId] ? "reported" : ""}`}
                        onClick={() => handleReportClick(visibleProfiles[currentIndex]?.userId)}
                        disabled={!!reportedProfiles[visibleProfiles[currentIndex]?.userId]}
                        title="Report"
                    >
                        <FaFlag size={24} />
                    </button>
                </div>
            )}

            {/* Share Popup */}
            {showSharePopup && (
                <>
                    <div className="blur-overlay"></div>
                    <div className="share-popup">
                        <div className="popup-content">
                            <h4>Share This Profile</h4>
                            <p>Copy the link below or share directly on WhatsApp:</p>
                            <input type="text" value={shareLink} readOnly />
                            <button className="copy-button" onClick={handleCopyLink}>Copy Link</button>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(
                                    `Check out this profile: ${shareLink}`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="whatsapp-share-button"
                            >
                                Share on WhatsApp
                            </a>
                            <button className="close-button" onClick={() => setShowSharePopup(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Report Popup */}
            {showReportPopup && (
                <>
                    <div className="blur-overlay2"></div>
                    <div className="report-popup">
                        <h4>Report Profile</h4>
                        <p>Select a reason for reporting:</p>
                        <select
                            value={selectedReportReason}
                            onChange={(e) => setSelectedReportReason(e.target.value)}
                        >
                            <option value="">Select a reason</option>
                            <option value="Sexual Content">Sexual Content</option>
                            <option value="Fake Profile">Fake Profile</option>
                            <option value="Harassment or Bullying">Harassment or Bullying</option>
                            <option value="Spam or Fraud">Spam or Fraud</option>
                            <option value="Harmful Content">Harmful Content</option>
                            <option value="Other">Other</option>
                        </select>
                        <p>Additional Details (Optional):</p>
                        <textarea
                            value={reportDetails}
                            onChange={(e) => setReportDetails(e.target.value)}
                            placeholder="Provide more details about the issue..."
                            rows={4}
                            cols={40}
                            className="report-textarea"
                        />
                        <button className="submit-report-button" onClick={handleReportSubmit}>
                            Submit Report
                        </button>
                        <button className="close-button2" onClick={() => setShowReportPopup(false)}>
                            Cancel
                        </button>
                    </div>
                </>
            )}

            {/* Match Modal */}
            {showMatchModal && (
                <MatchModal
                    userName={matchedUserName}
                    matchedUserId={matchedUserId}
                    matchedUserEmail={matchedUserEmail}
                    onClose={() => setShowMatchModal(false)}
                    onViewProfile={() => setShowMatchModal(false)}
                />
            )}
        </div>
    );
};

export default SuggestedProfiles;